import { useState, useEffect, useCallback } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Plus, Search, MoreHorizontal, CheckCircle2, Loader2, Trash2,
  LayoutList, Columns3, User as UserIcon, Calendar, Link2, Settings2, X, GripVertical, Info,
  ArrowUpDown, ArrowUp, ArrowDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { SavedViews } from '../components/SavedViews';
import { HelpPanel } from '../components/ui/help-panel';
import { taskManagerHelp } from '../lib/helpContent';
import { ColorPicker } from '../components/ui/color-picker';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { useAuth } from '../context/AuthContext';
import BrandEmptyState from '../components/landing/BrandEmptyState';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const API_AVATAR_URL = (userId) => `${API_URL}/api/users/${userId}/avatar`;

export default function TaskManager() {
  const { currentWorkspace } = useWorkspace();
  const { user: currentUser } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [members, setMembers] = useState([]);
  const [memberColors, setMemberColors] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('kanban');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterAssignee, setFilterAssignee] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', status: 'todo', priority: 'normal', category: '', assignee_id: '', due_date: '' });

  // List view sort
  const [listSort, setListSort] = useState('status');
  const [listSortDir, setListSortDir] = useState('asc');
  const handleListSort = (field) => {
    if (listSort === field) setListSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setListSort(field); setListSortDir('asc'); }
  };
  const ListSortIcon = ({ field }) => {
    if (listSort !== field) return <ArrowUpDown className="w-3 h-3 text-muted-foreground/40" />;
    return listSortDir === 'asc' ? <ArrowUp className="w-3 h-3 text-primary" /> : <ArrowDown className="w-3 h-3 text-primary" />;
  };

  // Settings form state
  const [newStatus, setNewStatus] = useState({ key: '', label: '', color: '#6b7280' });
  const [newPriority, setNewPriority] = useState({ key: '', label: '', color: '#6b7280' });
  const [newCategory, setNewCategory] = useState({ name: '', color: '#6b7280' });

  const wsId = currentWorkspace?.workspace_id;

  // Build lookup maps from dynamic data
  const statusMap = {};
  statuses.forEach(s => { statusMap[s.key] = s; });
  const priorityMap = {};
  priorities.forEach(p => { priorityMap[p.key] = p; });
  const categoryColorMap = {};
  categories.forEach(c => { categoryColorMap[c.name] = c.color || '#6b7280'; });

  const fetchAll = useCallback(async () => {
    if (!wsId) return;
    try {
      const [tasksRes, catsRes, statusRes, prioRes, membersRes, colorsRes, statsRes] = await Promise.all([
        apiFetch(`${API_URL}/api/workspaces/${wsId}/tasks`),
        apiFetch(`${API_URL}/api/workspaces/${wsId}/task-categories`),
        apiFetch(`${API_URL}/api/workspaces/${wsId}/task-statuses`),
        apiFetch(`${API_URL}/api/workspaces/${wsId}/task-priorities`),
        apiFetch(`${API_URL}/api/workspaces/${wsId}/members`),
        apiFetch(`${API_URL}/api/workspaces/${wsId}/member-colors`),
        apiFetch(`${API_URL}/api/workspaces/${wsId}/tasks/stats`),
      ]);
      if (tasksRes.ok) setTasks(await tasksRes.json());
      if (catsRes.ok) setCategories(await catsRes.json());
      if (statusRes.ok) setStatuses(await statusRes.json());
      if (prioRes.ok) setPriorities(await prioRes.json());
      if (membersRes.ok) setMembers(await membersRes.json());
      if (colorsRes.ok) setMemberColors(await colorsRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [wsId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = tasks.filter(t => {
    if (search && !t.title.toLowerCase().includes(search.toLowerCase()) && !(t.description || '').toLowerCase().includes(search.toLowerCase())) return false;
    if (filterCategory !== 'all' && t.category !== filterCategory) return false;
    if (filterAssignee !== 'all' && t.assignee_id !== filterAssignee) return false;
    return true;
  });

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      const url = editingTask ? `${API_URL}/api/workspaces/${wsId}/tasks/${editingTask.task_id}` : `${API_URL}/api/workspaces/${wsId}/tasks`;
      const res = await apiFetch(url, { method: editingTask ? 'PATCH' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      if (res.ok) { toast.success(editingTask ? 'Task updated' : 'Task created'); setDialogOpen(false); setEditingTask(null); fetchAll(); }
    } catch (e) { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const updateStatus = async (taskId, newStatus) => {
    await apiFetch(`${API_URL}/api/workspaces/${wsId}/tasks/${taskId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: newStatus }) });
    fetchAll();
  };

  const deleteTask = async (taskId) => {
    await apiFetch(`${API_URL}/api/workspaces/${wsId}/tasks/${taskId}`, { method: 'DELETE' });
    fetchAll(); toast.success('Task deleted');
  };

  const openEdit = (task) => {
    setEditingTask(task);
    setForm({ title: task.title, description: task.description || '', status: task.status, priority: task.priority, category: task.category || '', assignee_id: task.assignee_id || '', due_date: task.due_date || '' });
    setDialogOpen(true);
  };

  const openNew = () => {
    setEditingTask(null);
    // Smart defaults: first available category, current user as assignee
    const defaultCategory = categories[0]?.name || '';
    const defaultAssignee = members.find(m => m.user_id === currentUser?.user_id)?.user_id
      || members[0]?.user_id
      || currentUser?.user_id
      || '';
    setForm({
      title: '',
      description: '',
      status: statuses[0]?.key || 'todo',
      priority: priorities.find(p => p.key === 'normal')?.key || priorities[0]?.key || 'normal',
      category: defaultCategory,
      assignee_id: defaultAssignee,
      due_date: '',
    });
    setDialogOpen(true);
  };

  // Quick inline category creation from the New Task dialog
  const quickCreateCategory = async () => {
    const name = window.prompt('New category name:');
    if (!name || !name.trim()) return;
    const res = await apiFetch(
      `${API_URL}/api/workspaces/${wsId}/task-categories`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), color: '#6b7280' }),
      }
    );
    if (res.ok) {
      const created = await res.json();
      // refresh the categories list AND select the new one in the form
      setCategories(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      setForm(f => ({ ...f, category: created.name }));
      toast.success(`Category "${created.name}" added`);
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || 'Could not create category');
    }
  };

  // ─── Settings handlers ───
  const addStatus = async () => {
    if (!newStatus.label.trim()) return;
    const key = newStatus.key.trim() || newStatus.label.trim().toLowerCase().replace(/\s+/g, '_');
    const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/task-statuses`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ key, label: newStatus.label, color: newStatus.color }) });
    if (res.ok) { setNewStatus({ key: '', label: '', color: '#6b7280' }); fetchAll(); toast.success('Status added'); }
    else { const e = await res.json(); toast.error(e.detail || 'Failed'); }
  };
  const deleteStatus = async (id) => { await apiFetch(`${API_URL}/api/workspaces/${wsId}/task-statuses/${id}`, { method: 'DELETE' }); fetchAll(); };
  const updateStatusColor = async (id, color) => {
    await apiFetch(`${API_URL}/api/workspaces/${wsId}/task-statuses/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ color }) });
    fetchAll();
  };
  const addPriority = async () => {
    if (!newPriority.label.trim()) return;
    const key = newPriority.key.trim() || newPriority.label.trim().toLowerCase().replace(/\s+/g, '_');
    const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/task-priorities`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ key, label: newPriority.label, color: newPriority.color }) });
    if (res.ok) { setNewPriority({ key: '', label: '', color: '#6b7280' }); fetchAll(); toast.success('Priority added'); }
    else { const e = await res.json(); toast.error(e.detail || 'Failed'); }
  };
  const deletePriority = async (id) => { await apiFetch(`${API_URL}/api/workspaces/${wsId}/task-priorities/${id}`, { method: 'DELETE' }); fetchAll(); };
  const updatePriorityColor = async (id, color) => {
    await apiFetch(`${API_URL}/api/workspaces/${wsId}/task-priorities/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ color }) });
    fetchAll();
  };
  const addCategory = async () => {
    if (!newCategory.name.trim()) return;
    const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/task-categories`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newCategory) });
    if (res.ok) { setNewCategory({ name: '', color: '#6b7280' }); fetchAll(); toast.success('Category added'); }
    else { const e = await res.json(); toast.error(e.detail || 'Failed'); }
  };
  const deleteCategory = async (id) => { await apiFetch(`${API_URL}/api/workspaces/${wsId}/task-categories/${id}`, { method: 'DELETE' }); fetchAll(); };
  const updateCategoryColor = async (id, color) => {
    await apiFetch(`${API_URL}/api/workspaces/${wsId}/task-categories/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ color }) });
    fetchAll();
  };
  const setMemberColor = async (userId, color) => {
    await apiFetch(`${API_URL}/api/workspaces/${wsId}/member-colors/${userId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ color }) });
    setMemberColors(prev => ({ ...prev, [userId]: color }));
  };

  // ─── Drag and Drop ───
  const onDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId) return;
    // Optimistically update UI
    setTasks(prev => prev.map(t => t.task_id === draggableId ? { ...t, status: destination.droppableId } : t));
    // Update backend
    updateStatus(draggableId, destination.droppableId);
  };

  // ─── Assignee color dot ───
  const AssigneeDot = ({ userId, name, size = 'sm' }) => {
    const color = memberColors[userId] || '#6b7280';
    const sz = size === 'sm' ? 'w-5 h-5 text-[9px]' : 'w-7 h-7 text-[11px]';
    const avatarSz = size === 'sm' ? 'w-5 h-5' : 'w-7 h-7';
    return (
      <Avatar className={cn(avatarSz, "flex-shrink-0")} title={name || ''}>
        <AvatarImage src={API_AVATAR_URL(userId)} alt={name} className="object-cover" />
        <AvatarFallback className={cn("font-bold text-white", sz === 'w-5 h-5 text-[9px]' ? 'text-[9px]' : 'text-[11px]')} style={{ backgroundColor: color }}>
          {(name || '?').charAt(0).toUpperCase()}
        </AvatarFallback>
      </Avatar>
    );
  };

  // ─── Task Card ───
  const TaskCard = ({ task }) => {
    const st = statusMap[task.status] || { label: task.status, color: '#6b7280' };
    const pr = priorityMap[task.priority] || { label: task.priority, color: '#6b7280' };
    const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== (statuses.find(s => s.key === 'done')?.key || 'done');
    const catColor = categoryColorMap[task.category] || '#6b7280';

    return (
      <Card className="hover:border-primary/30 transition-colors group" data-testid={`task-${task.task_id}`}>
        <CardContent className="p-3 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-medium flex-1">{task.title}</p>
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"><MoreHorizontal className="w-3.5 h-3.5" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openEdit(task)}>Edit</DropdownMenuItem>
                <DropdownMenuSeparator />
                {statuses.filter(s => s.key !== task.status).map(s => (
                  <DropdownMenuItem key={s.key} onClick={() => updateStatus(task.task_id, s.key)}>
                    <span className="w-2 h-2 rounded-full mr-2 flex-shrink-0" style={{ backgroundColor: s.color }} />
                    Move to {s.label}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-destructive" onClick={() => deleteTask(task.task_id)}>Delete</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {task.description && <p className="text-xs text-muted-foreground line-clamp-2">{task.description}</p>}
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium" style={{ backgroundColor: pr.color + '20', color: pr.color }}>{pr.label}</span>
            {task.category && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium border" style={{ borderColor: catColor + '60', color: catColor }}>{task.category}</span>}
            {task.due_date && (
              <span className={cn("text-[10px] flex items-center gap-0.5", isOverdue ? "text-red-600 font-medium" : "text-muted-foreground")}>
                <Calendar className="w-3 h-3" />{task.due_date}
              </span>
            )}
          </div>
          <div className="flex items-center justify-between">
            {task.assignee_id && (
              <div className="flex items-center gap-1.5">
                <AssigneeDot userId={task.assignee_id} name={task.assignee_name} />
                <span className="text-[10px] text-muted-foreground">{task.assignee_name}</span>
              </div>
            )}
            {task.linked_contact_name && (
              <span className="text-[10px] text-muted-foreground flex items-center gap-1 ml-auto"><Link2 className="w-3 h-3" />{task.linked_contact_name}</span>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-64" /><div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-48" />)}</div></div>;

  return (
    <div className="space-y-6" data-testid="task-manager">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Task Manager</h1>
          <p className="text-muted-foreground text-sm">Manage and assign work across your team</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)} data-testid="task-settings-btn">
            <Settings2 className="w-4 h-4 mr-1" /> Customize
          </Button>
          <div className="flex border rounded-lg overflow-hidden">
            <Button variant={view === 'kanban' ? 'default' : 'ghost'} size="sm" onClick={() => setView('kanban')} className="rounded-none" data-testid="view-kanban">
              <Columns3 className="w-4 h-4 mr-1" /> Kanban
            </Button>
            <Button variant={view === 'list' ? 'default' : 'ghost'} size="sm" onClick={() => setView('list')} className="rounded-none" data-testid="view-list">
              <LayoutList className="w-4 h-4 mr-1" /> List
            </Button>
          </div>
          <Button onClick={openNew} data-testid="new-task-btn"><Plus className="w-4 h-4 mr-1" /> New Task</Button>
          <HelpPanel {...taskManagerHelp} />
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="flex flex-wrap gap-3">
          {statuses.map(s => (
            <TooltipProvider key={s.key}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Card className="border-l-4 min-w-[100px] cursor-help" style={{ borderLeftColor: s.color }}>
                    <CardContent className="p-3 text-center">
                      <p className="text-2xl font-bold">{stats[s.key] || 0}</p>
                      <p className="text-xs text-muted-foreground">{s.label}</p>
                    </CardContent>
                  </Card>
                </TooltipTrigger>
                <TooltipContent><p className="text-xs">Tasks currently in "{s.label}" status</p></TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
        </div>
      )}

      {/* Saved Views + Filters */}
      <div className="space-y-3">
        <SavedViews
          page="tasks"
          presets={[
            { name: 'My Tasks', icon: 'users', filters: { assignee: '__me__' }, sort: { field: 'due_date', dir: 'asc' } },
            { name: 'Urgent', icon: 'alert', filters: { category: 'all', assignee: 'all' }, sort: { field: 'priority', dir: 'desc' } },
            { name: 'Due Today', icon: 'calendar', filters: { category: 'all', assignee: 'all' }, sort: { field: 'due_date', dir: 'asc' } },
            { name: 'Overdue', icon: 'clock', filters: { category: 'all', assignee: 'all' }, sort: { field: 'due_date', dir: 'asc' } },
          ]}
          onApply={({ filters, sort }) => {
            if (filters.category) setFilterCategory(filters.category);
            if (filters.assignee) setFilterAssignee(filters.assignee);
            if (sort.field) { setListSort(sort.field); setListSortDir(sort.dir || 'asc'); }
          }}
          currentFilters={{ category: filterCategory, assignee: filterAssignee }}
          currentSort={{ field: listSort, dir: listSortDir }}
        />
        <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search tasks..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="task-search" />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-40" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(c => <SelectItem key={c.category_id} value={c.name}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterAssignee} onValueChange={setFilterAssignee}>
          <SelectTrigger className="w-40" data-testid="filter-assignee"><SelectValue placeholder="Assignee" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Members</SelectItem>
            {members.map(m => <SelectItem key={m.user_id} value={m.user_id}>
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: memberColors[m.user_id] || '#6b7280' }} />{m.name}</div>
            </SelectItem>)}
          </SelectContent>
        </Select>
        {/* Member color legend */}
        {members.length > 1 && (
          <div className="flex items-center gap-1.5 ml-auto">
            {members.map(m => (
              <div key={m.user_id} className="flex items-center gap-1" title={m.name}>
                <AssigneeDot userId={m.user_id} name={m.name} />
              </div>
            ))}
          </div>
        )}
        </div>
      </div>

      {/* First-time empty state — only when no tasks AT ALL, no filters/search applied */}
      {!loading && tasks.length === 0 && !search && filterCategory === 'all' && filterAssignee === 'all' && (
        <BrandEmptyState
          overline="Tasks · Get Started"
          title="Move work forward, faster."
          accent="forward, faster."
          description="Drag-and-drop kanban, customizable categories, assignees, due dates. Built for households, businesses, and accounting firms — your way."
          icon={CheckCircle2}
          tone="emerald"
          primaryCta={{ label: 'Create your first task', onClick: openNew, testId: 'empty-create-task' }}
          secondaryCta={{ label: 'Customize columns', onClick: () => setSettingsOpen(true), testId: 'empty-customize-tasks' }}
          testId="tasks-empty-state"
        />
      )}

      {/* Kanban View */}
      {view === 'kanban' && (tasks.length > 0 || search || filterCategory !== 'all' || filterAssignee !== 'all') && (
        <div>
          <div className="flex items-center gap-2 mb-3 text-xs text-muted-foreground">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-muted/50 border border-dashed cursor-help" data-testid="dnd-hint">
                    <GripVertical className="w-3 h-3" />
                    <span>Drag cards between columns to change status</span>
                    <Info className="w-3 h-3 ml-1 opacity-50" />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p className="text-xs max-w-[200px]">Click and hold any task card, then drag it to another column to update its status instantly.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <DragDropContext onDragEnd={onDragEnd}>
            <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${statuses.length}, minmax(0, 1fr))` }} data-testid="kanban-board">
              {statuses.map(st => {
                const colTasks = filtered.filter(t => t.status === st.key);
                return (
                  <div key={st.key} className="space-y-3 min-w-0">
                    <div className="flex items-center gap-2 pb-2 border-b">
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: st.color }} />
                      <span className="text-sm font-semibold truncate">{st.label}</span>
                      <Badge variant="secondary" className="text-[10px] ml-auto">{colTasks.length}</Badge>
                    </div>
                    <Droppable droppableId={st.key}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={cn(
                            "space-y-2 min-h-[80px] rounded-lg p-1 transition-colors",
                            snapshot.isDraggingOver && "bg-primary/5 ring-2 ring-primary/20 ring-dashed"
                          )}
                        >
                          {colTasks.map((task, index) => (
                            <Draggable key={task.task_id} draggableId={task.task_id} index={index}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className={cn(snapshot.isDragging && "opacity-80 rotate-1 shadow-lg")}
                                >
                                  <TaskCard task={task} />
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                          {colTasks.length === 0 && !snapshot.isDraggingOver && (
                            <div className="text-center py-6 text-xs text-muted-foreground border border-dashed rounded-lg">No tasks</div>
                          )}
                        </div>
                      )}
                    </Droppable>
                  </div>
                );
              })}
            </div>
          </DragDropContext>
        </div>
      )}

      {/* List View */}
      {view === 'list' && (tasks.length > 0 || search || filterCategory !== 'all' || filterAssignee !== 'all') && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="cursor-pointer select-none" onClick={() => handleListSort('title')}><span className="flex items-center gap-1">Task <ListSortIcon field="title" /></span></TableHead>
                  <TableHead className="w-28 cursor-pointer select-none" onClick={() => handleListSort('status')}><span className="flex items-center gap-1">Status <ListSortIcon field="status" /></span></TableHead>
                  <TableHead className="w-24 cursor-pointer select-none" onClick={() => handleListSort('priority')}><span className="flex items-center gap-1">Priority <ListSortIcon field="priority" /></span></TableHead>
                  <TableHead className="w-32 cursor-pointer select-none" onClick={() => handleListSort('category')}><span className="flex items-center gap-1">Category <ListSortIcon field="category" /></span></TableHead>
                  <TableHead className="w-32 cursor-pointer select-none" onClick={() => handleListSort('assignee_name')}><span className="flex items-center gap-1">Assignee <ListSortIcon field="assignee_name" /></span></TableHead>
                  <TableHead className="w-28 cursor-pointer select-none" onClick={() => handleListSort('due_date')}><span className="flex items-center gap-1">Due Date <ListSortIcon field="due_date" /></span></TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length > 0 ? [...filtered].sort((a, b) => {
                  const valA = (a[listSort] || '').toString().toLowerCase();
                  const valB = (b[listSort] || '').toString().toLowerCase();
                  if (valA < valB) return listSortDir === 'asc' ? -1 : 1;
                  if (valA > valB) return listSortDir === 'asc' ? 1 : -1;
                  return 0;
                }).map(task => {
                  const st = statusMap[task.status] || { label: task.status, color: '#6b7280' };
                  const pr = priorityMap[task.priority] || { label: task.priority, color: '#6b7280' };
                  return (
                    <TableRow key={task.task_id} data-testid={`task-row-${task.task_id}`}>
                      <TableCell>
                        <p className="font-medium text-sm">{task.title}</p>
                        {task.description && <p className="text-xs text-muted-foreground line-clamp-1">{task.description}</p>}
                      </TableCell>
                      <TableCell><span className="inline-flex items-center gap-1.5 text-[11px] font-medium"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: st.color }} />{st.label}</span></TableCell>
                      <TableCell><span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ backgroundColor: pr.color + '20', color: pr.color }}>{pr.label}</span></TableCell>
                      <TableCell className="text-sm">{task.category || '-'}</TableCell>
                      <TableCell>
                        {task.assignee_id ? (
                          <div className="flex items-center gap-1.5">
                            <AssigneeDot userId={task.assignee_id} name={task.assignee_name} />
                            <span className="text-sm">{task.assignee_name}</span>
                          </div>
                        ) : <span className="text-sm text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell className="text-sm">{task.due_date || '-'}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7"><MoreHorizontal className="w-3.5 h-3.5" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEdit(task)}>Edit</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {statuses.filter(s => s.key !== task.status).map(s => (
                              <DropdownMenuItem key={s.key} onClick={() => updateStatus(task.task_id, s.key)}>
                                <span className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: s.color }} />Move to {s.label}
                              </DropdownMenuItem>
                            ))}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive" onClick={() => deleteTask(task.task_id)}>Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                }) : (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No tasks found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingTask ? 'Edit Task' : 'New Task'}</DialogTitle>
            <DialogDescription>{editingTask ? 'Update task details' : 'Create a new task for your team'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="e.g., Reconcile Q1 bank statements" data-testid="task-title-input" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Details..." rows={3} data-testid="task-desc-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                  <SelectTrigger data-testid="task-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {statuses.map(s => <SelectItem key={s.key} value={s.key}><div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />{s.label}</div></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}>
                  <SelectTrigger data-testid="task-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {priorities.map(p => <SelectItem key={p.key} value={p.key}><div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />{p.label}</div></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Category</Label>
                  <button
                    type="button"
                    onClick={quickCreateCategory}
                    className="text-xs text-primary hover:underline inline-flex items-center gap-0.5"
                    data-testid="task-category-quick-create"
                  >
                    <Plus className="w-3 h-3" /> New
                  </button>
                </div>
                {categories.length === 0 ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={quickCreateCategory}
                    className="w-full justify-start text-muted-foreground"
                    data-testid="task-category-empty-cta"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1.5" /> Create your first category
                  </Button>
                ) : (
                  <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                    <SelectTrigger data-testid="task-category"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>{categories.map(c => <SelectItem key={c.category_id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>
              <div className="space-y-2">
                <Label>Assign To</Label>
                <Select value={form.assignee_id} onValueChange={v => setForm({...form, assignee_id: v})}>
                  <SelectTrigger data-testid="task-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    {/* Always include the current user even if they're not in members yet (active = available assignee) */}
                    {currentUser?.user_id && !members.some(m => m.user_id === currentUser.user_id) && (
                      <SelectItem key={currentUser.user_id} value={currentUser.user_id} data-testid="task-assignee-self">
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full bg-primary" />
                          {currentUser.name || currentUser.email} (You)
                        </div>
                      </SelectItem>
                    )}
                    {members.map(m => (
                      <SelectItem key={m.user_id} value={m.user_id}>
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: memberColors[m.user_id] || '#6b7280' }} />
                          {m.name}{m.user_id === currentUser?.user_id ? ' (You)' : ''}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Due Date</Label>
              <Input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} data-testid="task-due-date" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-task-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              {editingTask ? 'Update' : 'Create Task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="task-settings-dialog">
          <DialogHeader>
            <DialogTitle>Customize Task Manager</DialogTitle>
            <DialogDescription>Configure statuses, priorities, categories, and team colors</DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {/* Statuses */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Statuses (Kanban Columns)</h3>
              <div className="space-y-1.5">
                {statuses.map(s => (
                  <div key={s.status_id} className="flex items-center gap-2 p-2 rounded-lg border bg-muted/30">
                    <ColorPicker value={s.color} onChange={(c) => updateStatusColor(s.status_id, c)} />
                    <span className="text-sm font-medium flex-1">{s.label}</span>
                    <span className="text-[10px] text-muted-foreground font-mono">{s.key}</span>
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => deleteStatus(s.status_id)} data-testid={`delete-status-${s.key}`}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Input placeholder="Label (e.g., Blocked)" value={newStatus.label} onChange={e => setNewStatus({...newStatus, label: e.target.value, key: e.target.value.toLowerCase().replace(/\s+/g, '_')})} className="flex-1" data-testid="new-status-label" />
                <ColorPicker value={newStatus.color} onChange={(c) => setNewStatus({...newStatus, color: c})} />
                <Button size="sm" onClick={addStatus} data-testid="add-status-btn"><Plus className="w-3 h-3 mr-1" />Add</Button>
              </div>
            </div>

            {/* Priorities */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Priorities</h3>
              <div className="space-y-1.5">
                {priorities.map(p => (
                  <div key={p.priority_id} className="flex items-center gap-2 p-2 rounded-lg border bg-muted/30">
                    <ColorPicker value={p.color} onChange={(c) => updatePriorityColor(p.priority_id, c)} />
                    <span className="text-sm font-medium flex-1">{p.label}</span>
                    <span className="text-[10px] text-muted-foreground font-mono">{p.key}</span>
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => deletePriority(p.priority_id)} data-testid={`delete-priority-${p.key}`}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Input placeholder="Label (e.g., Critical)" value={newPriority.label} onChange={e => setNewPriority({...newPriority, label: e.target.value, key: e.target.value.toLowerCase().replace(/\s+/g, '_')})} className="flex-1" data-testid="new-priority-label" />
                <ColorPicker value={newPriority.color} onChange={(c) => setNewPriority({...newPriority, color: c})} />
                <Button size="sm" onClick={addPriority} data-testid="add-priority-btn"><Plus className="w-3 h-3 mr-1" />Add</Button>
              </div>
            </div>

            {/* Categories */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Categories</h3>
              <div className="flex flex-wrap gap-1.5">
                {categories.map(c => (
                  <div key={c.category_id} className="flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-full border text-xs font-medium" style={{ borderColor: (c.color || '#6b7280') + '60' }}>
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color || '#6b7280' }} />
                    {c.name}
                    <button onClick={() => deleteCategory(c.category_id)} className="w-4 h-4 rounded-full hover:bg-destructive/10 flex items-center justify-center"><X className="w-2.5 h-2.5 text-destructive" /></button>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Input placeholder="New category" value={newCategory.name} onChange={e => setNewCategory({...newCategory, name: e.target.value})} className="flex-1" data-testid="new-category-name" />
                <ColorPicker value={newCategory.color} onChange={(c) => setNewCategory({...newCategory, color: c})} />
                <Button size="sm" onClick={addCategory} data-testid="add-category-btn"><Plus className="w-3 h-3 mr-1" />Add</Button>
              </div>
            </div>

            {/* Team Colors */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Team Member Colors</h3>
              <p className="text-xs text-muted-foreground">Assign colors so tasks are visually identifiable by assignee</p>
              <div className="space-y-2">
                {members.map(m => (
                  <div key={m.user_id} className="flex items-center gap-3 p-2 rounded-lg border bg-muted/30" data-testid={`member-color-${m.user_id}`}>
                    <AssigneeDot userId={m.user_id} name={m.name} size="md" />
                    <span className="text-sm font-medium flex-1">{m.name}</span>
                    <span className="text-[10px] text-muted-foreground capitalize">{m.role}</span>
                    <ColorPicker value={memberColors[m.user_id] || '#6b7280'} onChange={(c) => setMemberColor(m.user_id, c)} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
